import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dfrApi from '../../services/dfrApi';
import '../../styles/dfr.css';

const TONE = {
  success: '#22c55e',
  danger: '#ef4444',
  info: '#38bdf8',
  warning: '#eab308',
};

const STATUS_CLASS = {
  removed: 'dfr-status-removed',
  awaiting_verification: 'dfr-status-awaiting',
  requested: 'dfr-status-suppression',
  reappeared: 'dfr-status-reappeared',
  in_progress: 'dfr-status-progress',
  queued: 'dfr-status-progress',
  suppressed: 'dfr-status-removed',
  monitoring: 'dfr-status-suppression',
  blocked: 'dfr-status-reappeared',
  excluded: 'dfr-status-awaiting',
};

const STATUS_LABEL = {
  removed: 'Removed',
  awaiting_verification: 'Awaiting Verification',
  requested: 'Suppression Requested',
  reappeared: 'Reappeared',
  in_progress: 'In Progress',
  queued: 'Queued',
  suppressed: 'Suppressed',
  monitoring: 'Monitoring',
  blocked: 'Blocked',
  excluded: 'Excluded',
};

function DfrOverview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [caseId, setCaseId] = useState('');
  const [seriesDays, setSeriesDays] = useState(365);
  const [statusFilter, setStatusFilter] = useState('');
  const [analystFilter, setAnalystFilter] = useState('');
  const [search, setSearch] = useState('');
  const [reportBusy, setReportBusy] = useState(false);

  const load = useCallback(async (id, days = seriesDays) => {
    setLoading(true);
    setError('');
    try {
      const res = await dfrApi.overview(id || undefined, days);
      setPayload(res.data);
      if (res.data.case?.id) setCaseId(res.data.case.id);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || err.message || 'Failed to load DFR overview');
    } finally {
      setLoading(false);
    }
  }, [seriesDays]);

  useEffect(() => {
    load();
  }, [load]);

  const caseData = payload?.case;
  const k = payload?.kpis || {};
  const categories = payload?.categories || [];
  const maxCat = Math.max(1, ...categories.map((c) => c.count));
  const analysts = useMemo(
    () =>
      [...new Set((payload?.operations || []).map((o) => o.analyst).filter(Boolean))].sort(),
    [payload]
  );

  const rows = (payload?.operations || []).filter((o) => {
    if (statusFilter && o.status !== statusFilter) return false;
    if (analystFilter && o.analyst !== analystFilter) return false;
    if (
      search &&
      !`${o.source} ${o.exposure_type} ${o.analyst || ''}`.toLowerCase().includes(search.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const onGenerateReport = async () => {
    if (!caseData?.id) return;
    setReportBusy(true);
    try {
      const res = await dfrApi.createReport({ case_id: caseData.id, days: seriesDays });
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(res.data.html || '<p>Report created</p>');
        w.document.close();
      }
    } catch (err) {
      window.alert(err.response?.data?.error || 'Report generation failed');
    } finally {
      setReportBusy(false);
    }
  };

  const formatWhen = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 19);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const rangeLabel =
    seriesDays >= 365 ? 'Last 12 Months' : seriesDays >= 90 ? 'Last 90 Days' : 'Last 30 Days';

  if (loading && !payload) {
    return (
      <div className="dfr-page">
        <header className="main-header">
          <h1 className="page-title">Digital Footprint Reduction</h1>
        </header>
        <div className="main-body">
          <div className="card"><div className="card-body">Loading DFR workspace…</div></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dfr-page">
        <header className="main-header">
          <h1 className="page-title">Digital Footprint Reduction</h1>
        </header>
        <div className="main-body">
          <div className="card"><div className="card-body" style={{ color: '#f87171' }}>{error}</div></div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="dfr-page">
        <header className="main-header">
          <h1 className="page-title">Digital Footprint Reduction</h1>
        </header>
        <div className="main-body">
          <div className="card">
            <div className="card-body dfr-stub">
              <h2>No DFR cases yet</h2>
              <p>Create a client and case under Clients / Exposure Cases.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sweepLabel = caseData.next_sweep_at
    ? formatWhen(caseData.next_sweep_at)
    : 'Not scheduled';

  return (
    <div className="dfr-page">
      <header className="main-header">
        <div>
          <h1 className="page-title">Digital Footprint Reduction</h1>
          <div className="dfr-subtitle">
            <span>Broker removals</span>
            <span>Evidence preservation</span>
            <span>Continuous monitoring</span>
          </div>
        </div>
        <div className="header-actions">
          <select
            className="dfr-client-select"
            value={caseId}
            onChange={(e) => {
              setCaseId(e.target.value);
              load(e.target.value, seriesDays);
            }}
            aria-label="Case"
          >
            {(payload.cases || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="main-body">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Sources Scanned</div>
            <div className="stat-value">{k.sources_scanned ?? 0}</div>
          </div>
          <div className="stat-card cyan">
            <div className="stat-label">Exposures Confirmed</div>
            <div className="stat-value">{k.exposures_confirmed ?? 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Removed</div>
            <div className="stat-value">{k.removed ?? 0}</div>
          </div>
          <div className="stat-card orange">
            <div className="stat-label">In Progress</div>
            <div className="stat-value">{k.in_progress ?? 0}</div>
          </div>
          <div className="stat-card purple">
            <div className="stat-label">Monitoring</div>
            <div className="stat-value">{k.monitoring ?? 0}</div>
          </div>
          <div className="stat-card cyan">
            <div className="stat-label">Reduction</div>
            <div className="stat-value">{k.reduction_pct ?? 0}%</div>
          </div>
          <div className="stat-card orange">
            <div className="stat-label">Unreviewed Scan Hits</div>
            <div className="stat-value">{k.unreviewed_scan_hits ?? 0}</div>
          </div>
        </div>

        <div className="dfr-grid-main">
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <h3 className="card-title">Exposure Reduction Over Time</h3>
                <select
                  className="dfr-client-select"
                  value={seriesDays}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSeriesDays(next);
                    load(caseId, next);
                  }}
                  aria-label="Time range"
                  style={{ minWidth: 140 }}
                >
                  <option value={30}>Last 30 Days</option>
                  <option value={90}>Last 90 Days</option>
                  <option value={365}>Last 12 Months</option>
                </select>
              </div>
              <div className="card-body chart-card-body" style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={payload.reduction_series || []}>
                    <defs>
                      <linearGradient id="dfrConfirmed" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="dfrResidual" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7dd3fc" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#7dd3fc" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        background: '#121a2a',
                        border: '1px solid rgba(148,163,184,0.2)',
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="confirmed" name="Confirmed Exposure" stroke="#2563eb" fill="url(#dfrConfirmed)" strokeWidth={2} />
                    <Area type="monotone" dataKey="residual" name="Residual Exposure" stroke="#7dd3fc" fill="url(#dfrResidual)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
                  {rangeLabel}
                  {caseData.monitoring_ends_at
                    ? ` · Monitoring term ends ${formatWhen(caseData.monitoring_ends_at)}`
                    : ''}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Removal Operations</h3>
              </div>
              <div className="card-body">
                <div className="dfr-filter-bar">
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">Status</option>
                    {Object.entries(STATUS_LABEL).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <select value={analystFilter} onChange={(e) => setAnalystFilter(e.target.value)}>
                    <option value="">Assigned Analyst</option>
                    {analysts.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                  <input
                    type="search"
                    placeholder="Search source, type…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="dfr-table-wrap">
                  <table className="dfr-ops-table">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Exposure Type</th>
                        <th>Status</th>
                        <th>Submitted</th>
                        <th>Removed</th>
                        <th>Last Check</th>
                        <th>Evidence</th>
                        <th>Analyst</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((o) => (
                        <tr key={o.id}>
                          <td style={{ fontWeight: 600 }}>{o.source}</td>
                          <td style={{ color: 'var(--text-muted)' }}>{o.exposure_type}</td>
                          <td>
                            <span className={`dfr-status ${STATUS_CLASS[o.status] || 'dfr-status-progress'}`}>
                              {STATUS_LABEL[o.status] || o.status}
                            </span>
                          </td>
                          <td style={{ color: 'var(--text-muted)' }}>{formatWhen(o.submitted)}</td>
                          <td style={{ color: 'var(--text-muted)' }}>{formatWhen(o.removed_at)}</td>
                          <td style={{ color: 'var(--text-muted)' }}>{formatWhen(o.last_check)}</td>
                          <td style={{ color: 'var(--text-muted)' }}>{o.evidence_count}</td>
                          <td>{o.analyst || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="dfr-stack">
            <div className="card">
              <div className="card-header"><h3 className="card-title">Exposure by Category</h3></div>
              <div className="card-body">
                {categories.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>No category data</div>}
                {categories.map((c) => (
                  <div className="dfr-cat-row" key={c.name}>
                    <span>{c.name}</span>
                    <div className="dfr-bar-track">
                      <div className="dfr-bar-fill" style={{ width: `${(c.count / maxCat) * 100}%` }} />
                    </div>
                    <span>{c.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-header" style={{ justifyContent: 'center' }}>
                <h3 className="card-title">Case Health</h3>
              </div>
              <div className="card-body">
                <div className="dfr-risk-pair">
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Initial Risk</span>
                    <strong className="dfr-risk-high">{caseData.initial_risk}</strong>
                    <span style={{ color: 'var(--text-muted)' }}>{caseData.initial_risk >= 60 ? 'High' : 'Moderate'}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Current Risk</span>
                    <strong className="dfr-risk-low">{caseData.current_risk}</strong>
                    <span style={{ color: 'var(--text-muted)' }}>{caseData.current_risk <= 30 ? 'Low' : 'Moderate'}</span>
                  </div>
                </div>
                <div className="dfr-risk-gauge">
                  <div className="dfr-risk-marker" style={{ left: `${Math.min(100, caseData.current_risk)}%` }} />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3 className="card-title">Next Monitoring Sweep</h3></div>
              <div className="card-body">
                <div className="dfr-sweep-date">{sweepLabel}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  {caseData.days_until_sweep == null
                    ? 'Schedule a sweep from Monitoring'
                    : caseData.days_until_sweep >= 0
                      ? `in ${caseData.days_until_sweep} days`
                      : `${Math.abs(caseData.days_until_sweep)} days overdue`}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3 className="card-title">Timeline ({rangeLabel})</h3></div>
              <div className="card-body">
                <ul className="dfr-activity">
                  {(payload.timeline || []).slice(0, 25).map((ev) => (
                    <li key={`${ev.kind}-${ev.ref_id}-${ev.at}`}>
                      <span className="dot" style={{ background: '#38bdf8' }} />
                      <span>{ev.title}</span>
                      <time>{formatWhen(ev.at)}</time>
                    </li>
                  ))}
                  {!(payload.timeline || []).length ? (
                    <li style={{ color: 'var(--text-muted)' }}>No dated events in this window yet.</li>
                  ) : null}
                </ul>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3 className="card-title">Recent Activity</h3></div>
              <div className="card-body">
                <ul className="dfr-activity">
                  {(payload.activity || []).map((a) => (
                    <li key={a.id}>
                      <span className="dot" style={{ background: TONE[a.tone] || '#8b9bb4' }} />
                      <span>{a.text}</span>
                      <time>{a.when_label || a.when}</time>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <button type="button" className="dfr-report-btn" onClick={onGenerateReport} disabled={reportBusy}>
              {reportBusy ? 'Generating…' : 'Generate Client Report'}
              <small>Include charts, removals, and evidence log</small>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DfrOverview;
